import { z } from 'zod'

export const loginFormSchema = z.object({
  email: z.string().email('Unesi validnu email adresu'),
  password: z.string().min(6, 'Lozinka mora imati bar 6 znakova'),
})

export type LoginFormValues = z.infer<typeof loginFormSchema>

export const registerFormSchema = z
  .object({
    firstName: z.string().trim().min(2, 'Ime mora imati bar 2 znaka').max(80, 'Ime je predugačko'),
    lastName: z
      .string()
      .trim()
      .min(2, 'Prezime mora imati bar 2 znaka')
      .max(80, 'Prezime je predugačko'),
    username: z
      .string()
      .min(3, 'Korisničko ime mora imati bar 3 znaka')
      .max(40, 'Korisničko ime je predugačko'),
    email: z.string().email('Unesi validnu email adresu'),
    password: z.string().min(6, 'Lozinka mora imati bar 6 znakova'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Lozinke se ne poklapaju',
    path: ['confirmPassword'],
  })

export type RegisterFormValues = z.infer<typeof registerFormSchema>

export const profileFormSchema = z.object({
  firstName: z.string().trim().min(2, 'Ime mora imati bar 2 znaka').max(80, 'Ime je predugačko'),
  lastName: z
    .string()
    .trim()
    .min(2, 'Prezime mora imati bar 2 znaka')
    .max(80, 'Prezime je predugačko'),
  username: z
    .string()
    .trim()
    .min(3, 'Korisničko ime mora imati bar 3 znaka')
    .max(40, 'Korisničko ime je predugačko'),
  email: z.string().trim().email('Unesi validnu email adresu'),
})

export type ProfileFormValues = z.infer<typeof profileFormSchema>

export const passwordChangeSchema = z
  .object({
    currentPassword: z.string().min(1, 'Unesi trenutnu lozinku'),
    newPassword: z.string().min(6, 'Nova lozinka mora imati bar 6 znakova'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Lozinke se ne poklapaju',
    path: ['confirmPassword'],
  })

export type PasswordChangeValues = z.infer<typeof passwordChangeSchema>

export const strapiLoginResponseSchema = z.object({
  jwt: z.string(),
  user: z.object({
    id: z.number(),
    username: z.string(),
    email: z.string(),
    confirmed: z.boolean(),
    blocked: z.boolean(),
  }),
})

export type StrapiLoginResponse = z.infer<typeof strapiLoginResponseSchema>

export const strapiUserWithRoleSchema = z.object({
  id: z.number(),
  username: z.string(),
  email: z.string(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  role: z.object({
    id: z.number(),
    name: z.string(),
    type: z.string(),
  }),
  // JSON polja sa korisničkog naloga (system preferences). Validiraju se
  // posebno pri čitanju jer Strapi vraća proizvoljan JSON.
  //
  // `needsProfile` (kategorije invaliditeta) namjerno NIJE ovdje: na nalogu žive
  // samo izvedena a11y podešavanja, ne i dijagnoza iz koje su izvedena. Zod
  // odbacuje nepoznate ključeve, pa polje ne ulazi u aplikaciju ni ako ga Strapi
  // i dalje vrati.
  a11ySettings: z.unknown().nullable().optional(),
  courseProgress: z.unknown().nullable().optional(),
})

export type StrapiUserWithRole = z.infer<typeof strapiUserWithRoleSchema>

export const userSessionSchema = z.object({
  userId: z.number(),
  email: z.string(),
  username: z.string(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  role: z.string(),
  jwt: z.string(),
  expiresAt: z.number(),
})

export type UserSession = z.infer<typeof userSessionSchema>

/**
 * Ime za prikaz: „Ime Prezime" ako postoji, inače korisničko ime.
 * Postojeće sesije (pre uvođenja imena) padaju na username dok se ne prijave ponovo.
 */
export function sessionDisplayName(session: {
  firstName?: string | null
  lastName?: string | null
  username: string
}): string {
  const full = [session.firstName, session.lastName].filter(Boolean).join(' ').trim()
  return full.length > 0 ? full : session.username
}

// Zadržano zbog kompatibilnosti — sesija je sada zajednička za sve uloge
// (učenik i edukator), a uloga se čuva u `role` polju.
export const edukatorSessionSchema = userSessionSchema
export type EdukatorSession = UserSession

export const EDUKATOR_ROLE = 'edukator'
